import React, { useEffect, useMemo, useRef, useState } from "react";
import { Target, Plus, Edit3, Trash2, Save, Download, PenLine, CheckCircle2, XCircle, MinusCircle, Award } from "lucide-react";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import SignaturePad from "@/components/SignaturePad";
import GermanDateInput from "@/components/GermanDateInput";

const CATEGORY_META = {
  fachlich:    { label: "Fachliche Kompetenz",     cls: "bg-[#00CCC9]/20 text-[#005E5C] border-[#00CCC9]/40" },
  bewerbung:   { label: "Bewerbung & Jobsuche",    cls: "bg-amber-100 text-amber-900 border-amber-300" },
  persoenlich: { label: "Persönliche Entwicklung", cls: "bg-violet-100 text-violet-900 border-violet-300" },
  sonstiges:   { label: "Sonstiges",               cls: "bg-slate-100 text-slate-700 border-slate-300" },
};

const PRIO_META = {
  high:   { label: "Hoch",    cls: "text-red-700 border-red-300 bg-red-50" },
  medium: { label: "Mittel",  cls: "text-slate-700 border-slate-300 bg-slate-50" },
  low:    { label: "Niedrig", cls: "text-slate-500 border-slate-200 bg-white" },
};

const ACHIEVE_META = {
  achieved:     { label: "erreicht",           cls: "bg-emerald-100 text-emerald-900 border-emerald-400", Icon: CheckCircle2 },
  partial:      { label: "teilweise erreicht", cls: "bg-amber-100 text-amber-900 border-amber-400",       Icon: MinusCircle },
  not_achieved: { label: "nicht erreicht",     cls: "bg-red-100 text-red-900 border-red-400",             Icon: XCircle },
};

/**
 * Zielvereinbarung Panel — works for both coach (with clientId) and client (readOnly).
 * Coach: create/edit/delete goals, complete offboarding, download PDF.
 * TN: view goals and sign the agreement digitally.
 */
export default function ClientGoalsPanel({ clientId, readOnly = false, clientName }) {
  const [goals, setGoals] = useState([]);
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);        // goal or {} for new
  const [signOpen, setSignOpen] = useState(false);
  const [offOpen, setOffOpen] = useState(false);

  const listUrl = clientId ? `/clients/${clientId}/goals` : `/me/goals`;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(listUrl);
      setGoals(data.goals || []);
      setAgreement(data.agreement || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  const remove = async (g) => {
    if (!confirm(`Ziel „${g.title}" löschen?`)) return;
    try {
      await api.delete(`/goals/${g.goal_id}`);
      toast.success("Ziel gelöscht");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const downloadPdf = async () => {
    if (!clientId) return;
    try {
      const res = await api.get(`/clients/${clientId}/goals/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `zielvereinbarung_${clientName || "klient"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const stats = useMemo(() => {
    const s = { total: goals.length, achieved: 0, partial: 0, not_achieved: 0, open: 0 };
    for (const g of goals) {
      if (g.achievement === "achieved") s.achieved++;
      else if (g.achievement === "partial") s.partial++;
      else if (g.achievement === "not_achieved") s.not_achieved++;
      else s.open++;
    }
    return s;
  }, [goals]);

  return (
    <div className="border border-slate-200 bg-white mb-6" data-testid="goals-panel">
      <div className="px-5 py-3 border-b border-slate-200 flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-1.5">
            <Target size={11} strokeWidth={1.5} /> Zielvereinbarung
          </div>
          <div className="font-heading text-xl font-light tracking-tighter text-slate-900">
            {readOnly ? "Meine Ziele" : "Ziele & Offboarding"}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap text-[11px]">
          {stats.total > 0 && (
            <>
              <span className="px-2 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-800">✓ {stats.achieved}</span>
              <span className="px-2 py-0.5 border border-amber-300 bg-amber-50 text-amber-800">◐ {stats.partial}</span>
              <span className="px-2 py-0.5 border border-red-300 bg-red-50 text-red-800">✗ {stats.not_achieved}</span>
              <span className="px-2 py-0.5 border border-slate-200 bg-slate-50 text-slate-600">◯ {stats.open} offen</span>
            </>
          )}
          {!readOnly && clientId && (
            <>
              <button onClick={downloadPdf}
                className="px-3 py-1.5 text-[11px] font-medium border border-slate-300 hover:bg-slate-900 hover:text-white transition-colors flex items-center gap-1"
                data-testid="goals-pdf-btn">
                <Download size={11} strokeWidth={1.5} /> PDF
              </button>
              <button onClick={() => setOffOpen(true)}
                disabled={goals.length === 0}
                className={`px-3 py-1.5 text-[11px] font-medium border transition-colors flex items-center gap-1 disabled:opacity-40 ${
                  goals.length > 0 && !agreement?.offboarding_completed_at
                    ? "border-amber-500 bg-amber-50 text-amber-900 hover:bg-amber-500 hover:text-white animate-pulse"
                    : "border-slate-900 bg-white text-slate-900 hover:bg-slate-900 hover:text-white"
                }`}
                data-testid="open-offboarding-btn">
                <Award size={11} strokeWidth={1.5} /> {goals.length > 0 && !agreement?.offboarding_completed_at ? "Zielerreichung bewerten" : "Offboarding"}
              </button>
              <button onClick={() => setEditing({})}
                className="px-3 py-1.5 text-[11px] font-medium bg-slate-900 text-white hover:bg-black flex items-center gap-1"
                data-testid="add-goal-btn">
                <Plus size={11} strokeWidth={2} /> Ziel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Agreement status banner */}
      {agreement?.signed_at ? (
        <div className="px-5 py-2 bg-emerald-50 border-b border-emerald-100 text-[12px] text-emerald-900 flex items-center gap-2">
          <PenLine size={12} strokeWidth={1.5} />
          <span>Zielvereinbarung unterschrieben am <b>{formatDeShort(agreement.signed_at.slice(0,10))}</b></span>
          {agreement.offboarding_completed_at && (
            <span className="ml-auto text-emerald-800">
              · Offboarding abgeschlossen am <b>{formatDeShort(agreement.offboarding_completed_at.slice(0,10))}</b>
            </span>
          )}
        </div>
      ) : (
        <div className="px-5 py-2 bg-amber-50 border-b border-amber-100 text-[12px] text-amber-900 flex items-center gap-2">
          <PenLine size={12} strokeWidth={1.5} />
          <span>Zielvereinbarung noch nicht unterschrieben.</span>
          {readOnly && goals.length > 0 && (
            <button onClick={() => setSignOpen(true)}
              className="ml-auto px-3 py-1 text-[11px] font-medium bg-slate-900 text-white hover:bg-black"
              data-testid="sign-goals-btn">
              Jetzt unterschreiben
            </button>
          )}
        </div>
      )}

      {!readOnly && goals.length > 0 && !agreement?.offboarding_completed_at && (
        <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-[12px] text-amber-900 flex items-center gap-2"
          data-testid="offboarding-pending-banner">
          <Award size={12} strokeWidth={1.5} />
          <span><b>Pflicht vor Rechnungsstellung:</b> Zielerreichung noch nicht bewertet. Rechnung kann erst erstellt werden, wenn das Offboarding hier abgeschlossen ist.</span>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500 py-6 text-center">Lädt…</div>
      ) : goals.length === 0 ? (
        <div className="p-8 text-center">
          <Target size={24} strokeWidth={1} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-600 mb-3">
            {readOnly
              ? "Dein Coach hat noch keine Ziele mit dir vereinbart."
              : "Noch keine Ziele. Lege gemeinsam mit dem TN SMART-Ziele fest."}
          </p>
          {!readOnly && (
            <button onClick={() => setEditing({})}
              className="px-4 py-2 text-xs font-medium bg-slate-900 text-white hover:bg-black flex items-center gap-1 mx-auto"
              data-testid="add-first-goal-btn">
              <Plus size={12} strokeWidth={2} /> Erstes Ziel anlegen
            </button>
          )}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {goals.map((g, idx) => (
            <GoalRow key={g.goal_id} goal={g} idx={idx + 1}
              readOnly={readOnly}
              onEdit={() => setEditing(g)} onRemove={() => remove(g)} />
          ))}
        </ul>
      )}

      {editing !== null && !readOnly && (
        <GoalDialog initial={editing} clientId={clientId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}

      {signOpen && readOnly && (
        <SignAgreementDialog onClose={() => setSignOpen(false)} onSigned={() => { setSignOpen(false); load(); }} />
      )}

      {offOpen && !readOnly && (
        <OffboardingDialog clientId={clientId} goals={goals}
          onClose={() => setOffOpen(false)}
          onSaved={() => { setOffOpen(false); load(); }} />
      )}
    </div>
  );
}

function GoalRow({ goal, idx, readOnly, onEdit, onRemove }) {
  const cat = CATEGORY_META[goal.category] || CATEGORY_META.sonstiges;
  const prio = PRIO_META[goal.priority] || PRIO_META.medium;
  const ach = goal.achievement ? ACHIEVE_META[goal.achievement] : null;
  return (
    <li className="px-5 py-4 grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-3 items-start" data-testid={`goal-row-${goal.goal_id}`}>
      <div className="text-xs font-mono text-slate-400 pt-0.5">#{idx}</div>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <span className={`text-[10px] px-1.5 py-0.5 border ${cat.cls}`}>{cat.label}</span>
          <span className={`text-[10px] px-1.5 py-0.5 border ${prio.cls}`}>Prio: {prio.label}</span>
          {goal.deadline && (
            <span className="text-[10px] text-slate-500">bis {formatDeShort(goal.deadline)}</span>
          )}
          {ach && (
            <span className={`text-[10px] px-1.5 py-0.5 border inline-flex items-center gap-1 ${ach.cls}`}>
              <ach.Icon size={10} strokeWidth={2} /> {ach.label}
            </span>
          )}
        </div>
        <div className="text-sm font-medium text-slate-900">{goal.title}</div>
        {goal.description && (
          <div className="text-[12px] text-slate-600 mt-0.5 whitespace-pre-wrap">{goal.description}</div>
        )}
        {goal.achievement_note && (
          <div className="mt-2 text-[11px] text-slate-500 italic border-l-2 border-slate-300 pl-2">
            Bewertung: {goal.achievement_note}
          </div>
        )}
      </div>
      {!readOnly && (
        <div className="inline-flex gap-1">
          <button onClick={onEdit}
            className="p-1.5 border border-slate-300 hover:bg-slate-100"
            data-testid={`edit-goal-${goal.goal_id}`}>
            <Edit3 size={12} strokeWidth={1.5} />
          </button>
          <button onClick={onRemove}
            className="p-1.5 border border-slate-300 text-red-700 hover:bg-red-50"
            data-testid={`delete-goal-${goal.goal_id}`}>
            <Trash2 size={12} strokeWidth={1.5} />
          </button>
        </div>
      )}
    </li>
  );
}

function GoalDialog({ initial, clientId, onClose, onSaved }) {
  const isEdit = !!initial.goal_id;
  const [form, setForm] = useState({
    title: initial.title || "",
    description: initial.description || "",
    category: initial.category || "fachlich",
    priority: initial.priority || "medium",
    deadline: initial.deadline || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Bitte einen Titel eingeben"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        priority: form.priority,
        deadline: form.deadline || null,
      };
      if (isEdit) {
        await api.patch(`/goals/${initial.goal_id}`, payload);
      } else {
        await api.post(`/clients/${clientId}/goals`, payload);
      }
      toast.success(isEdit ? "Ziel aktualisiert" : "Ziel angelegt");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">
            {isEdit ? "Ziel bearbeiten" : "Neues Ziel"}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            SMART formulieren: spezifisch, messbar, erreichbar, relevant, terminiert.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Field label="Titel des Ziels *">
            <input value={form.title} onChange={(e) => set("title", e.target.value)}
              className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
              placeholder="z. B. Portfolio-Website online stellen"
              data-testid="goal-title-input" />
          </Field>

          <Field label="Beschreibung (SMART)">
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)}
              rows={3}
              className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
              placeholder="Spezifisch · Messbar · Erreichbar · Relevant · Terminiert"
              data-testid="goal-description-input" />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="Kategorie">
              <select value={form.category} onChange={(e) => set("category", e.target.value)}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
                data-testid="goal-category-select">
                {Object.entries(CATEGORY_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Priorität">
              <select value={form.priority} onChange={(e) => set("priority", e.target.value)}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
                data-testid="goal-priority-select">
                {Object.entries(PRIO_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Deadline">
              <GermanDateInput value={form.deadline || ""} onChange={(e) => set("deadline", e.target.value)}
                className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
                data-testid="goal-deadline-input" />
            </Field>
          </div>
        </div>

        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Abbrechen</button>
          <button onClick={save} disabled={saving}
            className="px-5 py-2 bg-slate-900 text-white text-sm font-medium hover:bg-black flex items-center gap-2 disabled:opacity-50"
            data-testid="save-goal-btn">
            <Save size={14} strokeWidth={1.5} /> {saving ? "Speichern…" : "Speichern"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SignAgreementDialog({ onClose, onSigned }) {
  const [signature, setSignature] = useState(null);
  const [saving, setSaving] = useState(false);
  const sigRef = useRef(null);

  const submit = async () => {
    if (!signature) { toast.error("Bitte hier unterschreiben"); return; }
    setSaving(true);
    try {
      await api.post(`/me/goals/sign`, { signature });
      toast.success("Zielvereinbarung unterschrieben — vielen Dank!");
      onSigned();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">
            Zielvereinbarung unterschreiben
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Mit deiner Unterschrift bestätigst du, dass du die aufgelisteten Ziele mit deinem Coach vereinbart hast.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <SignaturePad ref={sigRef} value={signature} onChange={(dataurl) => setSignature(dataurl)} testId="goal-signature-pad" />
          <p className="text-[11px] text-slate-500">Bitte unterschreibe im Feld oben mit Maus, Trackpad oder Finger.</p>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Abbrechen</button>
          <button onClick={submit} disabled={!signature || saving}
            className="px-5 py-2 bg-slate-900 text-white text-sm font-medium hover:bg-black flex items-center gap-2 disabled:opacity-50"
            data-testid="submit-goal-signature-btn">
            <PenLine size={14} strokeWidth={1.5} /> {saving ? "Speichern…" : "Unterschreiben"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OffboardingDialog({ clientId, goals, onClose, onSaved }) {
  const [items, setItems] = useState(() =>
    goals.map((g) => ({ goal_id: g.goal_id, achievement: g.achievement || "achieved", note: g.achievement_note || "" }))
  );
  const [summary, setSummary] = useState("");
  const [saving, setSaving] = useState(false);
  const setItem = (idx, patch) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/goals/offboarding`, { coach_summary: summary, items });
      toast.success("Offboarding abgeschlossen");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-light tracking-tighter">
            Offboarding · Zielerreichung bewerten
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Bewerte gemeinsam mit dem TN jedes Ziel. Diese Auswertung wird im PDF-Nachweis für den Träger dokumentiert.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {goals.map((g, idx) => (
            <div key={g.goal_id} className="border border-slate-200 p-3" data-testid={`offb-row-${g.goal_id}`}>
              <div className="text-sm font-medium text-slate-900 mb-2">{idx + 1}. {g.title}</div>
              <div className="flex gap-2 mb-2 flex-wrap">
                {[
                  { k: "achieved",     label: "✓ Erreicht",           cls: "border-emerald-400 bg-emerald-50 text-emerald-800" },
                  { k: "partial",      label: "◐ Teilweise erreicht", cls: "border-amber-400 bg-amber-50 text-amber-800" },
                  { k: "not_achieved", label: "✗ Nicht erreicht",     cls: "border-red-400 bg-red-50 text-red-800" },
                ].map((opt) => (
                  <button key={opt.k}
                    onClick={() => setItem(idx, { achievement: opt.k })}
                    className={`px-3 py-1 text-[11px] border transition-all ${items[idx].achievement === opt.k ? opt.cls + " font-semibold" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`offb-achievement-${g.goal_id}-${opt.k}`}>
                    {opt.label}
                  </button>
                ))}
              </div>
              <input value={items[idx].note} onChange={(e) => setItem(idx, { note: e.target.value })}
                className="w-full border border-slate-300 px-2 py-1.5 text-xs focus:outline-none focus:border-slate-900"
                placeholder="Kommentar zur Zielerreichung (optional)"
                data-testid={`offb-note-${g.goal_id}`} />
            </div>
          ))}
          <Field label="Zusammenfassung Coach (optional)">
            <textarea value={summary} onChange={(e) => setSummary(e.target.value)}
              rows={3}
              className="w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-slate-900"
              placeholder="Zusammenfassung der Maßnahme, Empfehlungen für die nächsten Schritte …"
              data-testid="offb-summary" />
          </Field>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">Abbrechen</button>
          <button onClick={save} disabled={saving}
            className="px-5 py-2 bg-slate-900 text-white text-sm font-medium hover:bg-black flex items-center gap-2 disabled:opacity-50"
            data-testid="submit-offboarding-btn">
            <Award size={14} strokeWidth={1.5} /> {saving ? "Speichern…" : "Offboarding abschließen"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
